import { Card, Loading, NotFound, useHasPermission, useIsAddonEnabled } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { Route, Routes, useParams } from "react-router";
import { EventUsersList } from "../../../../shared/events/eventUsers/EventUsersList";
import { ApplicationUserList } from "../../applications/ApplicationUserList";
import { LocalContactViewProvider } from "../../contactViews/local/LocalContactViewProvider";
import { eventService } from "../../shared/api/event/eventService";
import { AccessDeniedList } from "../../shared/ui/AccessDeniedList";
import { useGlobalTenants } from "../../tenants/global/GlobalTenantProvider";
import { ClippingProviderContactList } from "./ClippingProviderContactList";
import { EventCostCategoryList } from "./costCategories/EventCostCategoryList";
import { EventApplicationUsersHeader } from "./EventApplicationUsersHeader";
import { EventContactList } from "./eventContacts/EventContactList";
import { EventDetailsCard } from "./EventDetailsCard";
import { EventOverview } from "./EventOverview";
import { EventFeedbackPage } from "./feedback/EventFeedbackPage";
import { InvitationList } from "./invitationList/InvitationList";

export const EventDetailsPage: FC = () => {
	const { eventId = "" } = useParams<"eventId">();
	const { selected } = useGlobalTenants();
	const { data: event, isLoading } = useQuery({
		queryKey: eventService.detailKey(eventId),
		queryFn: () => eventService.detail(eventId).then((response) => response.data.data),
	});
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");
	const isContactDatabaseEnabled = useIsAddonEnabled("contactDatabase");
	const canReadContacts = useHasPermission(["contact.read"]);
	const canReadClippingProviderContacts = useHasPermission(["clippingProviderContact.read"]);
	const canReadCostCategories = useHasPermission(["costCategory.read"]) || !!event?.hasProjectMemberPermissions;
	const canReadFeedback = useHasPermission(["event.update"]) || !!event?.hasProjectManagerPermissions;
	const hasParticipantManagement = event && event.applications.some(({ type }) => type === "PARTICIPANT_MANAGEMENT");

	if (!event || isLoading) {
		return (
			<Card>
				<Loading />
			</Card>
		);
	}

	return (
		<Routes>
			<Route element={<EventOverview event={event} />}>
				<Route index path="details" element={<EventDetailsCard event={event} />} />
				<Route
					path="details/applications/:applicationId/users"
					element={
						<>
							<EventApplicationUsersHeader event={event} />
							<ApplicationUserList />
						</>
					}
				/>
				<Route
					path="users"
					element={
						<EventUsersList
							isEditableByUser={event.hasProjectManagerPermissions}
							keysToInvalidate={[eventService.indexKey]}
						/>
					}
				/>
				{hasParticipantManagement &&
					isContactDatabaseEnabled &&
					isPressModuleEnabled &&
					canReadClippingProviderContacts && (
						<Route path="clipping-provider-contacts" element={<ClippingProviderContactList />} />
					)}
				{hasParticipantManagement && isContactDatabaseEnabled && canReadContacts && (
					<Route
						path="contacts"
						element={
							<LocalContactViewProvider contactViewKey="eventContacts">
								{selected.tenant ? (
									<EventContactList eventId={eventId} eventContactService={eventService} />
								) : (
									<AccessDeniedList />
								)}
							</LocalContactViewProvider>
						}
					/>
				)}
				{isContactDatabaseEnabled && canReadContacts && (
					<Route
						path="invitation-list"
						element={
							<LocalContactViewProvider contactViewKey="invitationList">
								{selected.tenant ? <InvitationList /> : <AccessDeniedList />}
							</LocalContactViewProvider>
						}
					/>
				)}
				{canReadCostCategories && <Route path="cost-categories" element={<EventCostCategoryList />} />}
				{canReadFeedback && <Route path="feedback/*" element={<EventFeedbackPage event={event} />} />}
				<Route path="*" element={<NotFound />} />
			</Route>
		</Routes>
	);
};
